import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CalendarDays,
  Clock,
  History,
  RefreshCw,
  XCircle,
  CheckCircle,
  AlertCircle,
  Plus,
} from 'lucide-react';
import { Button, Badge, Modal, Input } from '@/components/ui';
import { JourneyTimeline } from '@/components/features/JourneyTimeline';
import { portalService } from '@/services/portalService';
import type { JourneyStepSummary } from '@/types/portal';
import type { JourneyStep, JourneyStepStatus } from '@/types/campaign';
import styles from './PatientDashboardPage.module.css';

type BadgeVariant = 'neutral' | 'success' | 'warning' | 'info' | 'error';

const STATUS_LABELS: Record<string, string> = {
  SCHEDULED: 'Agendado',
  CONFIRMED: 'Confirmado',
  WAITING: 'Em espera',
  IN_PROGRESS: 'Em andamento',
  COMPLETED: 'Concluído',
  CANCELLED: 'Cancelado',
  NO_SHOW: 'Não compareceu',
};

const STATUS_BADGE: Record<string, BadgeVariant> = {
  SCHEDULED: 'info',
  CONFIRMED: 'success',
  WAITING: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CANCELLED: 'error',
  NO_SHOW: 'error',
};

/**
 * Formats a date string (YYYY-MM-DD) to pt-BR locale.
 */
function formatDate(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  return date.toLocaleDateString('pt-BR', {
    weekday: 'short',
    day: '2-digit',
    month: 'long',
  });
}

/**
 * Converts JourneyStepSummary[] to JourneyStep[] for the JourneyTimeline component.
 */
function toJourneySteps(steps: JourneyStepSummary[]): JourneyStep[] {
  return steps.map((step) => ({
    id: step.stepId,
    journeyId: '',
    packageItemId: '',
    procedureTypeId: '',
    procedureTypeName: step.procedureTypeName,
    appointmentId: null,
    status: step.status as JourneyStepStatus,
    orderIndex: step.orderIndex,
    scheduledAt: step.scheduledAt ?? null,
    completedAt: step.completedAt ?? null,
  }));
}

/**
 * Patient portal dashboard page.
 *
 * Displays:
 * - Upcoming appointments with reschedule/cancel actions
 * - Past appointment history
 * - Active journey timeline (reuses JourneyTimeline component)
 *
 * Mobile-first card-based layout with clinic branding.
 */
export function PatientDashboardPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelError, setCancelError] = useState<string | null>(null);

  const {
    data: portal,
    isLoading,
    error: queryError,
  } = useQuery({
    queryKey: ['portal', 'my-portal'],
    queryFn: () => portalService.getMyPortal(),
  });

  const cancelMutation = useMutation({
    mutationFn: ({ appointmentId, reason }: { appointmentId: string; reason?: string }) =>
      portalService.selfCancel(appointmentId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal'] });
      closeCancelModal();
    },
    onError: () => {
      setCancelError('Erro ao cancelar agendamento. Tente novamente.');
    },
  });

  const openCancelModal = useCallback((appointmentId: string) => {
    setCancellingId(appointmentId);
    setCancelReason('');
    setCancelError(null);
  }, []);

  const closeCancelModal = useCallback(() => {
    setCancellingId(null);
    setCancelReason('');
    setCancelError(null);
  }, []);

  const handleCancel = useCallback(
    (event: React.FormEvent) => {
      event.preventDefault();
      if (!cancellingId) return;
      cancelMutation.mutate({
        appointmentId: cancellingId,
        reason: cancelReason.trim() || undefined,
      });
    },
    [cancellingId, cancelReason, cancelMutation],
  );

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.pageContent}>
          <div className={styles.loadingState} role="status" aria-busy="true">
            <p>Carregando seu painel...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (queryError) {
    return (
      <div className={styles.container}>
        <div className={styles.pageContent}>
          <div className={styles.errorState} role="alert">
            <AlertCircle size={32} aria-hidden="true" />
            <p>Erro ao carregar dados. Tente novamente mais tarde.</p>
          </div>
        </div>
      </div>
    );
  }

  // No data yet (no patientId set)
  if (!portal) {
    return (
      <div className={styles.container}>
        <div className={styles.pageContent}>
          <header className={styles.header}>
            <div className={styles.logo}>
              <span className={styles.logoIcon} aria-hidden="true">+</span>
              <span className={styles.logoText}>
                CRM<span className={styles.logoAccent}>Clinica</span>
              </span>
            </div>
            <h1 className={styles.title}>Meu Painel</h1>
          </header>
          <div className={styles.emptyState} role="status">
            <CalendarDays size={48} className={styles.emptyIcon} aria-hidden="true" />
            <p>Faça login para visualizar seus agendamentos.</p>
          </div>
        </div>
      </div>
    );
  }

  const { upcomingAppointments, pastAppointments, activeJourney } = portal;

  return (
    <div className={styles.container}>
      <div className={styles.pageContent}>
        <header className={styles.header}>
          <div className={styles.logo}>
            <span className={styles.logoIcon} aria-hidden="true">+</span>
            <span className={styles.logoText}>
              CRM<span className={styles.logoAccent}>Clinica</span>
            </span>
          </div>
          <h1 className={styles.title}>Ola, {portal.name}!</h1>
          <p className={styles.subtitle}>
            Gerencie seus agendamentos e acompanhe sua jornada.
          </p>
        </header>

        {/* Active Journey */}
        {activeJourney && (
          <section className={styles.section} aria-labelledby="journey-heading">
            <h2 id="journey-heading" className={styles.sectionTitle}>
              Minha Jornada
            </h2>
            <div className={styles.journeyCard}>
              <div className={styles.journeyHeader}>
                <span className={styles.journeyName}>{activeJourney.campaignName}</span>
                <Badge variant={activeJourney.status === 'COMPLETED' ? 'success' : 'info'}>
                  {activeJourney.status === 'IN_PROGRESS'
                    ? 'Em andamento'
                    : activeJourney.status === 'COMPLETED'
                      ? 'Concluida'
                      : activeJourney.status}
                </Badge>
              </div>
              <JourneyTimeline
                steps={toJourneySteps(activeJourney.steps)}
                ariaLabel={`Jornada: ${activeJourney.campaignName}`}
              />
            </div>
          </section>
        )}

        {/* Schedule action */}
        {activeJourney && activeJourney.status !== 'COMPLETED' && (
          <div className={styles.scheduleAction}>
            <Button
              onClick={() => navigate('/portal/schedule')}
              leftIcon={<Plus size={18} />}
              fullWidth
            >
              Agendar procedimento
            </Button>
          </div>
        )}

        {/* Upcoming Appointments */}
        <section className={styles.section} aria-labelledby="upcoming-heading">
          <h2 id="upcoming-heading" className={styles.sectionTitle}>
            <CalendarDays size={20} aria-hidden="true" />
            Próximos agendamentos
          </h2>

          {upcomingAppointments.length === 0 ? (
            <div className={styles.emptyCard} role="status">
              <Clock size={24} className={styles.emptyCardIcon} aria-hidden="true" />
              <p>Nenhum agendamento futuro.</p>
            </div>
          ) : (
            <ul className={styles.appointmentList} role="list">
              {upcomingAppointments.map((appointment) => (
                <li key={appointment.id} className={styles.appointmentCard}>
                  <div className={styles.appointmentInfo}>
                    <div className={styles.appointmentMain}>
                      <span className={styles.appointmentProcedure}>
                        {appointment.procedureTypeName}
                      </span>
                      <span className={styles.appointmentResource}>
                        {appointment.resourceName}
                      </span>
                    </div>
                    <div className={styles.appointmentMeta}>
                      <span className={styles.appointmentDate}>
                        <CalendarDays size={14} aria-hidden="true" />
                        {formatDate(appointment.date)}
                      </span>
                      <span className={styles.appointmentTime}>
                        <Clock size={14} aria-hidden="true" />
                        {appointment.startTime} - {appointment.endTime}
                      </span>
                    </div>
                    <Badge variant={STATUS_BADGE[appointment.status] ?? 'neutral'}>
                      {STATUS_LABELS[appointment.status] ?? appointment.status}
                    </Badge>
                  </div>
                  <div className={styles.appointmentActions}>
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<RefreshCw size={14} />}
                      onClick={() => navigate('/portal/schedule')}
                      aria-label={`Reagendar ${appointment.procedureTypeName}`}
                    >
                      Reagendar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      leftIcon={<XCircle size={14} />}
                      onClick={() => openCancelModal(appointment.id)}
                      aria-label={`Cancelar ${appointment.procedureTypeName}`}
                    >
                      Cancelar
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Past Appointments */}
        <section className={styles.section} aria-labelledby="past-heading">
          <h2 id="past-heading" className={styles.sectionTitle}>
            <History size={20} aria-hidden="true" />
            Histórico
          </h2>

          {pastAppointments.length === 0 ? (
            <div className={styles.emptyCard} role="status">
              <History size={24} className={styles.emptyCardIcon} aria-hidden="true" />
              <p>Nenhum agendamento anterior.</p>
            </div>
          ) : (
            <ul className={styles.appointmentList} role="list">
              {pastAppointments.map((appointment) => (
                <li key={appointment.id} className={styles.appointmentCardPast}>
                  <div className={styles.appointmentInfo}>
                    <div className={styles.appointmentMain}>
                      <span className={styles.appointmentProcedure}>
                        {appointment.procedureTypeName}
                      </span>
                      <span className={styles.appointmentResource}>
                        {appointment.resourceName}
                      </span>
                    </div>
                    <div className={styles.appointmentMeta}>
                      <span className={styles.appointmentDate}>
                        <CalendarDays size={14} aria-hidden="true" />
                        {formatDate(appointment.date)}
                      </span>
                      <span className={styles.appointmentTime}>
                        <Clock size={14} aria-hidden="true" />
                        {appointment.startTime} - {appointment.endTime}
                      </span>
                    </div>
                    <Badge variant={STATUS_BADGE[appointment.status] ?? 'neutral'}>
                      {STATUS_LABELS[appointment.status] ?? appointment.status}
                    </Badge>
                  </div>
                  {appointment.status === 'COMPLETED' && (
                    <CheckCircle size={18} className={styles.completedIcon} aria-hidden="true" />
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      {/* Cancel modal */}
      <Modal
        isOpen={cancellingId !== null}
        onClose={closeCancelModal}
        title="Cancelar agendamento"
        size="sm"
      >
        <form onSubmit={handleCancel} className={styles.cancelForm} noValidate>
          <p className={styles.cancelMessage}>
            Tem certeza que deseja cancelar este agendamento?
          </p>
          <Input
            label="Motivo (opcional)"
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
            placeholder="Informe o motivo do cancelamento"
            fullWidth
          />
          {cancelError && (
            <p className={styles.cancelError} role="alert">
              {cancelError}
            </p>
          )}
          <div className={styles.cancelActions}>
            <Button variant="ghost" type="button" onClick={closeCancelModal}>
              Voltar
            </Button>
            <Button variant="danger" type="submit" isLoading={cancelMutation.isPending}>
              Confirmar cancelamento
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
